import type { Space } from '@/types/spaces'

/*
 * F-203a: the retention period of an Attendance or Parcels space, stored in
 * `moduleConfig.<module>.retentionMonths`. Same bounds as the API (SEC-329:
 * 6 to 60 months, default 12); the server stays the judge, and it alone writes
 * `retentionMonthsPrevious` and `retentionMonthsEffectiveAt` (a shorter period
 * waits 7 days).
 */
export const RETENTION_MIN_MONTHS = 6
export const RETENTION_MAX_MONTHS = 60
export const RETENTION_DEFAULT_MONTHS = 12

export type RetentionModule = 'attendance' | 'parcels'

type RetentionSlice = { retentionMonths?: unknown; retentionMonthsEffectiveAt?: unknown } | null | undefined

const isMonths = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) >= RETENTION_MIN_MONTHS && (value as number) <= RETENTION_MAX_MONTHS

/** The configured period of a stored slice: the stored value, or the default. */
export const configuredMonths = (slice: RetentionSlice): number =>
  isMonths(slice?.retentionMonths) ? slice.retentionMonths : RETENTION_DEFAULT_MONTHS

/** The stored shortening still waiting for its date, or `null` (read from the space, not from the form). */
export const pendingShortening = (space: Space | undefined, module: RetentionModule, now = Date.now()): { months: number; at: string } | null => {
  const slice = (space?.moduleConfig as Record<string, RetentionSlice> | null | undefined)?.[module]
  const at = slice?.retentionMonthsEffectiveAt
  return typeof at === 'string' && Date.parse(at) > now ? { months: configuredMonths(slice), at } : null
}

/** The validation message key, or `null`: an empty field is an error, not "keep the default". */
export const retentionError = (value: unknown, t: (key: string) => string): string | null =>
  value === undefined || isMonths(value) ? null : t('common:retention.invalid')

/** `{ retentionMonths }` for the API, only when the field holds a valid value. */
export const retentionInput = (value: unknown): { retentionMonths?: number } => (isMonths(value) ? { retentionMonths: value } : {})

/** Whether saving `config` changes the period the space stores (then the team confirms first). */
export const retentionChanges = (module: RetentionModule, config: RetentionSlice, space: Space): boolean => {
  const stored = (space.moduleConfig as Record<string, RetentionSlice> | null | undefined)?.[module]
  return isMonths(config?.retentionMonths) && config.retentionMonths !== configuredMonths(stored)
}
